/** Hand-maintained types aligned with OpenAPI — run `pnpm generate` to refresh generated.ts */

import type { DocumentStatus } from "./document-status";
import { parseApiError } from "./api-error";

export type { DocumentStatus } from "./document-status";
export {
  DOCUMENT_STATUS_LABELS,
  documentStatusAllowsImprove,
  documentStatusAllowsSavePdf,
  isDocumentStatusTerminal,
} from "./document-status";
export { parseApiError, parseApiErrorBody, ApiError, type ParsedApiError } from "./api-error";
export type DocumentType =
  | "unknown"
  | "invoice"
  | "receipt"
  | "product"
  | "prescription"
  | "poster";
export type DataClassification = "public" | "personal" | "financial" | "health" | "legal";
export type LocaleTag = "en-US" | "fr" | "de" | "it" | "es" | "ru";

export type SubscriptionPlan = "free" | "personal" | "family" | "small_business" | "business";

export type StorageFormat = "legacy_plaintext" | "encrypted_v1";

export type CryptoAlgorithm = "dwim-aes256-gcm-v1";

export interface WrappedKeyPayload {
  algorithm: CryptoAlgorithm;
  nonce: string;
  wrapped_key: string;
}

export interface DocumentEncryptedBlobResponse {
  algorithm: CryptoAlgorithm;
  nonce: string;
  ciphertext: string;
  wrapped_dek: WrappedKeyPayload;
  content_mime_type: string;
  size_bytes: number;
}

export interface EncryptedPayload {
  algorithm: CryptoAlgorithm;
  nonce: string;
  ciphertext: string;
}

export interface DocumentExtractionUploadRequest {
  encrypted_extraction: EncryptedPayload;
  title?: string | null;
  doc_type?: DocumentType;
  classification?: DataClassification;
  tags?: string[];
  confidence?: number | null;
  content_hash?: string;
  /** Optional 1536-dim OpenAI embedding for semantic search (WP-027 / #75). */
  search_embedding?: number[] | null;
}

export interface DocumentExtractionUploadResponse {
  id: string;
  status: DocumentStatus;
  processed_at: string;
}

export interface UserCryptoStatusResponse {
  configured: boolean;
  crypto_kdf_salt: string | null;
  wrapped_umk_passphrase: WrappedKeyPayload | null;
}

export interface UserCryptoSetupRequest {
  crypto_kdf_salt: string;
  wrapped_umk_passphrase: WrappedKeyPayload;
}

export interface Tier3ConsentResponse {
  consented: boolean;
  consented_at: string | null;
}

/** @deprecated Use Tier3ConsentResponse (#87). */
export type Tier2ConsentResponse = Tier3ConsentResponse;

export type AiModelPlatform = "web" | "ios" | "android";

/**
 * What a model can be fed. Always includes "text"; "image" means it can take a
 * photograph, "file" that it can take a PDF. Either lets the client send the
 * document itself and skip the OCR step entirely.
 *
 * This exists because the pipeline has two independent axes - reading the pixels
 * and turning text into fields - and a vision model collapses them. A client that
 * cannot tell them apart sends an image to a text-only model, which does not fail:
 * it produces a confident, wrong answer.
 *
 * A PDF is a "file", not an "image". The difference is money: a model that lacks
 * "file" makes the provider parse the PDF first, which on a two-page invoice costs
 * about thirty times what the reading itself does.
 */
export type AiModelModality = "text" | "image" | "file";

export interface AiModel {
  id: string;
  name: string;
  description: string;
  platforms: AiModelPlatform[];
  /** False when the operating system picks the model, so there is nothing to choose. */
  selectable: boolean;
  /** Rough download weight in MB, for models the client fetches itself. Null when unknown. */
  approximate_download_mb: number | null;
  /** Never empty: a model that takes nothing cannot be sent anything. */
  input_modalities: AiModelModality[];
  /**
   * True when the model was described by the provider. False for bundled entries,
   * where the modalities are a hand-written assumption rather than a measurement.
   */
  modalities_authoritative?: boolean;
  /**
   * Measured line from the vision probe. Present on the models we ranked,
   * absent on the rest of the catalogue.
   */
  probe_summary?: string | null;
  /** Measured cost of one Tier 3 op, USD. Null when unmeasured. */
  estimated_cost_per_op_usd?: number | null;
  /**
   * True when the plan does not cover this model (expensive or unmeasured):
   * usage is billed on top, and the user is warned before the first choice.
   */
  usage_billed?: boolean;
}

/**
 * How the model picker narrows the catalogue. The reader picks a job — send the
 * original document, or keep the transcription step — rather than a modality name.
 */
export type DocumentCapabilityFilter = "any" | "direct" | "transcription";

/** Short labels for what a model can be given. `textOnly` means OCR still runs. */
export type ModelCapabilityBadge = "pdf" | "photo" | "textOnly";

/** True when the original PDF or photograph can be sent and OCR skipped. */
export function modelReadsDocumentDirectly(modalities: readonly AiModelModality[]): boolean {
  return modalities.includes("file") || modalities.includes("image");
}

export function modelMatchesCapabilityFilter(
  modalities: readonly AiModelModality[],
  filter: DocumentCapabilityFilter,
): boolean {
  if (filter === "any") return true;
  const direct = modelReadsDocumentDirectly(modalities);
  return filter === "direct" ? direct : !direct;
}

export function modelCapabilityBadges(
  modalities: readonly AiModelModality[],
): ModelCapabilityBadge[] {
  const badges: ModelCapabilityBadge[] = [];
  if (modalities.includes("file")) badges.push("pdf");
  if (modalities.includes("image")) badges.push("photo");
  return badges.length > 0 ? badges : ["textOnly"];
}

/**
 * OpenRouter's model page. Catalogue ids are already the `author/slug` path.
 * Anything else — a bare name, a space, a second slash — is not a page we can open.
 */
export function openRouterModelPageUrl(modelId: string): string | null {
  if (!/^[^/\s]+\/[^/\s]+$/.test(modelId)) return null;
  return `https://openrouter.ai/${modelId}`;
}

/**
 * Compare page for the first few catalogue ids.
 *
 * OpenRouter takes each id as two path segments, so the ids are joined as they
 * stand: `compare/openai/gpt-4o-mini/google/gemini-2.5-flash`. Two models is the
 * minimum that page can show; four keeps the URL readable.
 */
export function openRouterCompareUrl(modelIds: readonly string[]): string | null {
  const chosen: string[] = [];
  for (const id of modelIds) {
    if (!openRouterModelPageUrl(id) || chosen.includes(id)) continue;
    chosen.push(id);
    if (chosen.length === 4) break;
  }
  if (chosen.length < 2) return null;
  return `https://openrouter.ai/compare/${chosen.join("/")}`;
}

export interface AiModelTierCatalogue {
  models: AiModel[];
  /** The name to use when the user has not chosen one. */
  default_model: string;
  /** "provider" when fetched live, "fallback" or "bundled" when not. */
  source: string | null;
}

export interface AiModelsCatalogueResponse {
  platform: AiModelPlatform;
  on_device: AiModelTierCatalogue;
  server: AiModelTierCatalogue;
}

/** An OCR engine: what reads the document's pixels. See services/api/.../ocr_engines.py. */
export interface OcrEngine {
  id: string;
  name: string;
  description: string;
  platforms: AiModelPlatform[];
  /** False when the operating system provides it, so there is nothing to choose. */
  selectable: boolean;
  /** 0 for built-in engines; a browser engine that must be fetched says what it costs. */
  approximate_download_mb: number;
}

export interface OcrEnginesResponse {
  platform: AiModelPlatform;
  engines: OcrEngine[];
  /** The engine to use when the user has chosen nothing. */
  default_engine: string;
}

export interface UserOcrEngineResponse {
  ocr_engine: string;
  ocr_engine_is_default: boolean;
}

export interface UserAiModelsResponse {
  on_device_model: string;
  server_model: string;
  on_device_model_is_default: boolean;
  server_model_is_default: boolean;
  /** False until the user has accepted that a usage-billed model costs extra. */
  usage_billing_acknowledged?: boolean;
}

export interface UserAiModelsUpdateRequest {
  on_device_model?: string | null;
  server_model?: string | null;
  /** Set after showing the "this model is billed" warning, to record the choice. */
  acknowledge_usage_billing?: boolean;
}

export interface UserLocaleResponse {
  locale: LocaleTag | null;
}

export interface UserLocaleUpdateRequest {
  locale: LocaleTag | null;
}

export interface Tier2ExtractionPromptResponse {
  version: number;
  default_prompt: string;
  user_supplement: string | null;
  composed_prompt: string;
  tier3_extraction_template: string;
  tier3_reprompt_template: string;
  tier2_on_device_template: string;
  tier2_on_device_reprompt_template: string;
  default_upload_efforts: number[] | null;
  default_snap_efforts: number[] | null;
  web_default_upload_efforts: number[] | null;
  web_default_snap_efforts: number[] | null;
  mobile_default_upload_efforts: number[] | null;
  mobile_default_snap_efforts: number[] | null;
}

export interface UserExtractionSettingsUpdate {
  tier2_prompt_supplement?: string | null;
  default_upload_efforts?: number[] | null;
  default_snap_efforts?: number[] | null;
  web_default_upload_efforts?: number[] | null;
  web_default_snap_efforts?: number[] | null;
  mobile_default_upload_efforts?: number[] | null;
  mobile_default_snap_efforts?: number[] | null;
}

export interface DocumentViewCompilerConfigResponse {
  version: number;
  system_prompt: string;
  field_catalog: Record<string, unknown>;
}

export interface AdminHeuristicsEntry {
  key: string;
  kind: "text" | "json";
  default_value: unknown;
  override_value: unknown | null;
  effective_value: unknown;
  is_overridden: boolean;
  placeholders: string[];
}

export interface AdminHeuristicsSetupResponse {
  version: number;
  etag: string;
  items: AdminHeuristicsEntry[];
}

export interface AdminHeuristicsSetupUpdate {
  overrides: Record<string, string | null>;
}

export interface DocumentResponse {
  id: string;
  status: DocumentStatus;
  doc_type: DocumentType;
  classification: DataClassification;
  title: string | null;
  mime_type: string;
  size_bytes: number;
  storage_format?: StorageFormat;
  crypto_algorithm?: string | null;
  wrapped_dek?: WrappedKeyPayload | null;
  encrypted_extraction?: EncryptedPayload | null;
  extracted_data: Record<string, unknown> | null;
  tags: string[];
  confidence: number | null;
  effort?: number;
  source_document_id?: string | null;
  created_at: string;
  updated_at?: string | null;
  processed_at: string | null;
}

export type DocumentStorageFields = Pick<
  DocumentResponse,
  "storage_format" | "wrapped_dek"
>;

/** True when the document blob is stored without client-side encryption. */
export function isLegacyPlaintextDocument(document: DocumentStorageFields): boolean {
  if (document.storage_format === "encrypted_v1") {
    return false;
  }
  if (document.storage_format === "legacy_plaintext") {
    return true;
  }
  // Missing format: only legacy when there is no per-document encryption metadata.
  return document.wrapped_dek == null;
}

/** @deprecated Use {@link isLegacyPlaintextDocument} when `wrapped_dek` is available. */
export function isLegacyPlaintextStorage(
  storageFormat: StorageFormat | null | undefined,
): boolean {
  return storageFormat === "legacy_plaintext";
}

export interface DocumentListResponse {
  items: DocumentResponse[];
  total: number;
  limit: number;
  offset: number;
}

export type WorkItemStep =
  | "encrypt"
  | "upload"
  | "ocr"
  | "download_model"
  | "load_model"
  | "analyze"
  | "save";

export type WorkItemStatus =
  | "queued"
  | "claimed"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

export type WorkSessionStatus = "active" | "completed" | "failed" | "cancelled";

export type WorkItemKind = "upload_pipeline" | "extract_pending";

export type WorkClaimPolicy = "owner" | "pool";

export interface WorkLogEventResponse {
  id: string;
  work_item_id: string;
  step: WorkItemStep | null;
  message: string;
  fraction: number | null;
  created_at: string;
}

export interface WorkItemResponse {
  id: string;
  session_id: string;
  document_id: string | null;
  owner_client_id: string;
  claimed_by_client_id: string | null;
  kind: WorkItemKind;
  claim_policy: WorkClaimPolicy;
  status: WorkItemStatus;
  step: WorkItemStep;
  file_name: string;
  file_index: number;
  file_count: number;
  fraction: number;
  overall_fraction: number;
  message: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkSessionResponse {
  id: string;
  owner_client_id: string;
  label: string | null;
  status: WorkSessionStatus;
  created_at: string;
  updated_at: string;
  items: WorkItemResponse[];
}

export interface WorkSessionItemInput {
  file_name: string;
  file_index: number;
  file_count: number;
}

export interface WorkSessionCreateRequest {
  client_instance_id: string;
  label?: string | null;
  effort_levels?: number[] | null;
  items: WorkSessionItemInput[];
}

export interface WorkSessionCreateResponse {
  session: WorkSessionResponse;
  items: WorkItemResponse[];
}

export interface WorkActiveResponse {
  sessions: WorkSessionResponse[];
  items: WorkItemResponse[];
  log_events: WorkLogEventResponse[];
  failed_items: WorkItemResponse[];
}

export interface WorkClaimRequest {
  client_instance_id: string;
  max_items?: number;
}

export interface WorkClaimResponse {
  items: WorkItemResponse[];
}

export interface DocumentSyncStateResponse {
  revision: number;
  etag: string;
  has_non_terminal: boolean;
}

export interface DocumentCreateResponse {
  id: string;
  status: DocumentStatus;
  message: string;
  duplicate?: boolean;
}

export interface ReminderResponse {
  id: string;
  title: string;
  description: string | null;
  due_at: string;
  document_id: string | null;
  sent_at: string | null;
  created_at: string;
}

export interface ReminderListResponse {
  items: ReminderResponse[];
  total: number;
}

export interface Citation {
  document_id: string;
  title: string | null;
  excerpt: string;
}

export interface QueryDocumentContext {
  document_id: string;
  title: string | null;
  doc_type: DocumentType;
  ocr_text: string;
  extracted_data: Record<string, unknown>;
}

export interface QueryRequest {
  question: string;
  voice?: boolean;
  /** Client-decrypted extraction snippets (encrypted docs). Transient over TLS; not stored server-side. */
  document_context?: QueryDocumentContext[];
}

export interface QueryResponse {
  answer: string;
  /** BCP 47 language tag for the answer text (for TTS and UI). */
  answer_language: string;
  citations: Citation[];
}

/** Text + language tag returned from Ask Dwim search. */
/** What the user has agreed to for one rung of the Ask Dwim ladder. */
export type AskLadderConsentValue = "deny" | "ask" | "allow";
export type AskLadderSurface = "web" | "mobile";
export type AskLadderTier = "tier2" | "tier3";

export interface AskLadderConsentResponse {
  version: number;
  /** Effective consent per surface and tier, resolved from the surface default. */
  consent: Record<AskLadderSurface, Record<AskLadderTier, AskLadderConsentValue>>;
  /** The defaults, so a client can explain "never asked" rather than read it as deny. */
  defaults: Record<AskLadderSurface, Record<AskLadderTier, AskLadderConsentValue>>;
}

export interface SearchAnswer {
  text: string;
  language: string;
}

export const DEFAULT_ANSWER_LANGUAGE = "en-US";

/** Returned when the user has no ranked/analyzed documents to answer from (see API query heuristics). */
export const NO_ANALYZED_DOCUMENTS_ANSWER =
  "I don't have any analyzed documents yet. Snap an invoice or receipt first.";

export function isNoAnalyzedDocumentsAnswer(answer: string): boolean {
  const normalized = answer.trim();
  if (normalized === NO_ANALYZED_DOCUMENTS_ANSWER) {
    return true;
  }
  return /don't have any analyzed documents yet/i.test(normalized);
}

export interface SubscriptionResponse {
  plan: SubscriptionPlan;
  storage_used_bytes: number;
  storage_limit_bytes: number;
  docs_this_month: number;
  docs_limit: number | null;
  ai_queries_this_month: number;
  ai_queries_limit: number | null;
  region: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  stripe_portal_available: boolean;
  /** True when the plan may download reports as CSV (WP-050). */
  reports_export: boolean;
}

/** Stripe replaces `{CHECKOUT_SESSION_ID}` when redirecting after payment. */
export const CHECKOUT_SESSION_ID_PLACEHOLDER = "{CHECKOUT_SESSION_ID}";

export function checkoutSuccessUrl(webBaseUrl: string): string {
  const base = webBaseUrl.replace(/\/+$/, "");
  return `${base}/subscription/success?session_id=${CHECKOUT_SESSION_ID_PLACEHOLDER}`;
}

export function checkoutCancelUrl(webBaseUrl: string): string {
  const base = webBaseUrl.replace(/\/+$/, "");
  return `${base}/subscription/cancel`;
}

export interface CheckoutRequest {
  plan?: SubscriptionPlan;
  success_url: string;
  cancel_url: string;
}

export interface CheckoutResponse {
  checkout_url: string;
}

export interface SyncCheckoutRequest {
  session_id: string;
}

export interface AiQueryConsumeRequest {
  operation: string;
  idempotency_key?: string | null;
  preflight?: boolean;
}

export interface AiQueryUsageResponse {
  ai_queries_this_month: number;
  ai_queries_limit: number | null;
  plan: SubscriptionPlan;
}

/** API error codes that should show Subscribe instead of Retry (WP-048). */
export const BILLING_LIMIT_ERROR_CODES = [
  "storage_limit_exceeded",
  "document_limit_exceeded",
  "ai_limit_exceeded",
  "voice_limit_exceeded",
] as const;

export type BillingLimitErrorCode = (typeof BILLING_LIMIT_ERROR_CODES)[number];

export function isBillingLimitErrorCode(code: string | undefined): code is BillingLimitErrorCode {
  return (
    code !== undefined &&
    (BILLING_LIMIT_ERROR_CODES as readonly string[]).includes(code)
  );
}

/** Thrown when upload hits a 402 billing limit (storage, documents, AI). */
export class BillingLimitError extends Error {
  readonly code: BillingLimitErrorCode;

  constructor(code: BillingLimitErrorCode, message: string) {
    super(message);
    this.name = "BillingLimitError";
    this.code = code;
  }
}

export function isBillingLimitError(err: unknown): err is BillingLimitError {
  return err instanceof BillingLimitError;
}

/** Whether subscription usage still blocks the given billing limit (e.g. after upgrade). */
export function subscriptionStillAtBillingLimit(
  sub: SubscriptionResponse,
  code: BillingLimitErrorCode,
): boolean {
  switch (code) {
    case "document_limit_exceeded":
      return sub.docs_limit != null && sub.docs_this_month >= sub.docs_limit;
    case "storage_limit_exceeded":
      return sub.storage_used_bytes >= sub.storage_limit_bytes;
    case "ai_limit_exceeded":
      return (
        sub.ai_queries_limit != null && sub.ai_queries_this_month >= sub.ai_queries_limit
      );
    case "voice_limit_exceeded":
      return false;
    default:
      return false;
  }
}

export function billingLimitErrorFromHttp(
  status: number,
  body: string,
  message: string,
): BillingLimitError | null {
  if (status !== 402) {
    return null;
  }
  const parsed = parseApiError(body);
  if (parsed?.code && isBillingLimitErrorCode(parsed.code)) {
    return new BillingLimitError(parsed.code, message);
  }
  return null;
}

export interface PortalRequest {
  return_url: string;
}

export interface PortalResponse {
  portal_url: string;
}

export interface UserExportResponse {
  user_id: string;
  region: string;
  exported_at: string;
  documents: DocumentResponse[];
  reminders: ReminderResponse[];
}

export interface ClassificationPolicy {
  description: string;
  examples: string[];
  retention_days_free: number;
  encryption: string;
  requires_consent?: boolean;
}

export interface DataClassificationMatrix {
  classifications: Record<string, ClassificationPolicy>;
  regional_residency: Record<string, string>;
}

const unreadableDocuments = new Map<string, string>();
const unreadableListeners = new Set<() => void>();

/** Mark a document whose ciphertext cannot be decrypted with the current UMK. */
export function markDocumentUnreadable(documentId: string, reason?: string): void {
  const message = reason?.trim() || "Could not decrypt document";
  const previous = unreadableDocuments.get(documentId);
  unreadableDocuments.set(documentId, message);
  if (previous !== message) {
    for (const listener of unreadableListeners) {
      listener();
    }
  }
}

export function clearDocumentUnreadable(documentId: string): void {
  if (!unreadableDocuments.delete(documentId)) {
    return;
  }
  for (const listener of unreadableListeners) {
    listener();
  }
}

export function isDocumentUnreadable(documentId: string): boolean {
  return unreadableDocuments.has(documentId);
}

export function getDocumentUnreadableReason(documentId: string): string | null {
  return unreadableDocuments.get(documentId) ?? null;
}

export function clearDocumentUnreadableMarks(): number {
  const count = unreadableDocuments.size;
  unreadableDocuments.clear();
  for (const listener of unreadableListeners) {
    listener();
  }
  return count;
}

export function subscribeDocumentUnreadable(listener: () => void): () => void {
  unreadableListeners.add(listener);
  return () => {
    unreadableListeners.delete(listener);
  };
}

export const API_PATHS = {
  documents: "/v1/documents",
  documentsSyncState: "/v1/documents/sync-state",
  workSessions: "/v1/work/sessions",
  workActive: "/v1/work/active",
  workClaim: "/v1/work/items/claim",
  workItem: (id: string) => `/v1/work/items/${id}`,
  workItemHeartbeat: (id: string) => `/v1/work/items/${id}/heartbeat`,
  workItemEvents: (id: string) => `/v1/work/items/${id}/events`,
  workItemComplete: (id: string) => `/v1/work/items/${id}/complete`,
  workItemFail: (id: string) => `/v1/work/items/${id}/fail`,
  workItemsFailed: "/v1/work/items/failed",
  documentBlob: (id: string) => `/v1/documents/${id}/blob`,
  documentExtraction: (id: string) => `/v1/documents/${id}/extraction`,
  documentFile: (id: string) => `/v1/documents/${id}/file`,
  reminders: "/v1/reminders",
  query: "/v1/query",
  subscription: "/v1/subscription",
  subscriptionCheckout: "/v1/subscription/checkout",
  subscriptionSyncCheckout: "/v1/subscription/sync-checkout",
  subscriptionPortal: "/v1/subscription/portal",
  billingAiQuery: "/v1/billing/ai-query",
  privacyCrypto: "/v1/privacy/crypto",
  privacyExtractionRules: "/v1/privacy/extraction-rules",
  privacyClassificationCatalog: "/v1/privacy/classification-catalog",
  privacyClassificationRules: "/v1/privacy/classification-rules",
  privacyAskDwimRules: "/v1/privacy/ask-dwim-rules",
  privacyAskLadderConsent: "/v1/privacy/ask-ladder-consent",
  privacyExtractionPrompt: "/v1/privacy/extraction-prompt",
  privacyExtractionSettings: "/v1/privacy/extraction-settings",
  privacyDocumentViewCompilerConfig: "/v1/privacy/document-view-compiler-config",
  documentViews: "/v1/document-views",
  documentViewsRestoreDefaults: "/v1/document-views/restore-defaults",
  documentView: (id: string) => `/v1/document-views/${id}`,
  activeDocumentView: "/v1/users/me/active-document-view",
  reports: "/v1/reports",
  report: (id: string) => `/v1/reports/${id}`,
  privacyTier3Consent: "/v1/privacy/tier3-consent",
  /** @deprecated Use privacyTier3Consent (#87). */
  privacyTier2Consent: "/v1/privacy/tier2-consent",
  privacyExport: "/v1/privacy/export",
  privacyDelete: "/v1/privacy/account",
  pushToken: "/v1/privacy/push-token",
  processorCosts: "/v1/usage/processor-costs",
  extractionDevLogs: "/v1/dev/extraction-logs",
  extractionDevLogging: "/v1/dev/extraction-logging",
} as const;

/** A saved report as stored: the name and spec exist only inside `encrypted_spec`. */
export interface SavedReportResponse {
  id: string;
  wrapped_key: WrappedKeyPayload;
  encrypted_spec: EncryptedPayload;
  created_at: string;
  updated_at: string;
}

export interface SavedReportListResponse {
  items: SavedReportResponse[];
}

export interface SavedReportWriteRequest {
  wrapped_key: WrappedKeyPayload;
  encrypted_spec: EncryptedPayload;
}

export type ProcessorCostProcessor = "tier2_on_device" | "tier3_cloud" | "client_ocr";

export interface ProcessorCostEventInput {
  processor: ProcessorCostProcessor;
  cost_usd: number;
  document_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface ProcessorCostBatchRequest {
  events: ProcessorCostEventInput[];
}

export type ExtractionDevLogSource = "web" | "ios" | "android";
export type ExtractionDevLogOutcome = "compare" | "applied" | "rejected" | "failed";

export interface ExtractionDevLogCreateRequest {
  source: ExtractionDevLogSource;
  git_commit?: string | null;
  model_name?: string | null;
  tier: number;
  effort_level?: number | null;
  document_id?: string | null;
  confidence_before?: number | null;
  confidence_after?: number | null;
  metadata_before?: Record<string, unknown> | null;
  metadata_after?: Record<string, unknown> | null;
  input_prompt?: string | null;
  input_type?: string | null;
  raw_output?: string | null;
  compare_snapshot?: Record<string, unknown> | null;
  thumbnail_data_url?: string | null;
  outcome: ExtractionDevLogOutcome;
  latency_ms?: number | null;
}

export interface ExtractionDevLogCreateResponse {
  id: string;
}

export interface AdminExtractionDevLogEntry {
  id: string;
  user_id: string;
  document_id: string | null;
  source: ExtractionDevLogSource;
  git_commit: string | null;
  model_name: string | null;
  tier: number;
  effort_level: number | null;
  confidence_before: number | null;
  confidence_after: number | null;
  metadata_before: Record<string, unknown> | null;
  metadata_after: Record<string, unknown> | null;
  input_prompt: string | null;
  input_type: string | null;
  raw_output: string | null;
  compare_snapshot: Record<string, unknown> | null;
  thumbnail_data_url: string | null;
  observations: string | null;
  outcome: ExtractionDevLogOutcome;
  latency_ms: number | null;
  created_at: string;
}

export interface AdminExtractionDevLogList {
  items: AdminExtractionDevLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export type {
  CategoryCatalog,
  CategoryDefinition,
  ClassificationCatalogResponse,
  SemanticDate,
  SemanticLevel,
  SemanticMetadata,
} from "./semantic";
export type {
  ActiveDocumentViewResponse,
  ActiveDocumentViewUpdateRequest,
  ColorRule,
  ColorTarget,
  DocumentViewCompileStatus,
  DocumentViewCreateRequest,
  DocumentViewListResponse,
  DocumentViewResponse,
  DocumentViewSpec,
  DocumentViewUpdateRequest,
  FilterClause,
  FilterExpr,
  GridLayout,
  ListLayout,
  SortKey,
  ViewLayout,
  ViewSection,
} from "./document-views";
